import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { Supplier, SupplierPayload } from '../types/Supplier.types';
import { FinanceSupplier } from '../pages/DashboardPage/views/FinancesView/masters/financeMasters';
import { paymentMethodCode, paymentMethodName } from './paymentMethodCodes';
import { getAccessToken, buildQuery, unwrapList, unwrapRecord, unwrapError } from './apiUtils';

// Los .avap registran estas rutas con guion bajo (suppliers/list.avap:2), pero el
// resto del frontend llama a las suyas con guion (/client-list, /clients-create).
// Si el gateway rechaza estas, el cambio se hace aquí y en ningún otro sitio.
const ROUTES = {
  list:   '/suppliers_list',
  create: '/suppliers_create',
  update: '/suppliers_update'
};

export const mapSupplier = (raw: any): Supplier => ({
  id:      String(raw.id),
  name:    raw.name || '',
  cif:     raw.cif || '',
  email:   raw.email || '',
  phone:   raw.phone || '',
  address: raw.address || '',
  status:  raw.status === 'inactive' ? 'inactive' : 'active',
  notes:   raw.notes || '',
  paymentMethod:    raw.payment_method || '',
  paymentTermsDays: raw.payment_terms_days === null || raw.payment_terms_days === undefined
    ? null
    : Number(raw.payment_terms_days),
  bankAccount:      raw.bank_account || ''
});

export const listSuppliers = async (
  options: { status?: string; search?: string; page?: number; pageSize?: number } = {}
): Promise<Supplier[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token: token,
    status:       options.status,
    search:       options.search,
    page:         options.page || 1,
    page_size:    options.pageSize || 100
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.list}${query}`);
  return unwrapList(res).map(mapSupplier);
};

export const createSupplier = async (payload: SupplierPayload): Promise<Supplier> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token: token,
    name:    payload.name,
    cif:     payload.cif,
    email:   payload.email,
    phone:   payload.phone,
    address: payload.address,
    status:  payload.status || 'active',
    notes:   payload.notes,
    payment_method:     payload.paymentMethod,
    payment_terms_days: payload.paymentTermsDays,
    bank_account:       payload.bankAccount
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.create}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al dar de alta el acreedor.'));
  return mapSupplier(record);
};

export const updateSupplier = async (id: string, payload: Partial<SupplierPayload>): Promise<Supplier> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  // suppliers/update.avap solo toca los campos que llegan != None, así que
  // buildQuery omitiendo vacíos da exactamente la semántica de update parcial.
  const query = buildQuery({
    access_token: token,
    id,
    name:    payload.name,
    cif:     payload.cif,
    email:   payload.email,
    phone:   payload.phone,
    address: payload.address,
    status:  payload.status,
    notes:   payload.notes,
    payment_method:     payload.paymentMethod,
    payment_terms_days: payload.paymentTermsDays,
    bank_account:       payload.bankAccount
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.update}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al modificar el acreedor.'));
  return mapSupplier(record);
};

// No existe /suppliers_delete en el backend, así que la "baja" es lógica.
export const deactivateSupplier = (id: string): Promise<Supplier> =>
  updateSupplier(id, { status: 'inactive' });

const paymentTermsName = (days: number | null): string =>
  days === null || isNaN(days) ? 'Neto 30 días' : days === 0 ? 'Inmediato (Contado)' : `Neto ${days} días`;

/**
 * La tabla `suppliers` no guarda tipo (proveedor/acreedor), persona de contacto,
 * dirección desglosada, cuenta PGC, límite de crédito ni adjuntos: son del
 * maestro de Financiero y quedan con valores neutros al recargar.
 */
export const toFinanceSupplier = (s: Supplier): FinanceSupplier => ({
  id:                     s.id,
  name:                   s.name,
  cif:                    s.cif,
  type:                   'Proveedor',
  contactPerson:          '',
  email:                  s.email,
  phone:                  s.phone,
  street:                 s.address,
  city:                   '',
  postalCode:             '',
  province:               '',
  country:                'España',
  paymentTerms:           paymentTermsName(s.paymentTermsDays),
  preferredPaymentMethod: paymentMethodName(s.paymentMethod),
  account:                '400000',
  creditLimit:            0,
  notes:                  s.notes,
  attachments:            [],
  pendingBalance:         0,
  status:                 s.status === 'inactive' ? 'Inactivo' : 'Activo'
});

/** Datos del formulario de Financiero → campos que sí guarda el backend. */
export const toSupplierPayload = (
  form: { name: string; cif: string; email: string; phone: string; address: string; notes: string; status: FinanceSupplier['status']; preferredPaymentMethod: string },
  paymentTermsDays: number
): SupplierPayload => ({
  name:             form.name,
  cif:              form.cif,
  email:            form.email,
  phone:            form.phone,
  address:          form.address,
  notes:            form.notes,
  status:           form.status === 'Inactivo' ? 'inactive' : 'active',
  paymentMethod:    paymentMethodCode(form.preferredPaymentMethod),
  paymentTermsDays
});
